import { useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { toast } from 'sonner';
import { Package, AlertTriangle, Plus, Minus, Trash2, RefreshCw, Snowflake, ThermometerSnowflake, Salad, Archive, GlassWater, Candy, Pencil, Tags, Check, X, Clock, Ban, Save, Upload, Camera, Eye, Loader2, Euro, Truck, History } from 'lucide-react';
import { Checkbox } from '../components/ui/checkbox';
import { ExportButtons } from '../components/ExportButtons';
import { SnapshotButtons } from '../components/SnapshotButtons';
import { ValorisationTab } from '../components/ValorisationTab';
import { ReceptionsTab } from '../components/ReceptionsTab';
import { HistoriqueTab } from '../components/HistoriqueTab';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from '../components/ui/alert-dialog';
import { ScrollArea } from '../components/ui/scroll-area';

const ZONE_CONFIG = {
  'Congelateur': { icon: Snowflake, color: 'text-blue-500' },
  'Frigo': { icon: ThermometerSnowflake, color: 'text-cyan-500' },
  'Saladette': { icon: Salad, color: 'text-green-500' },
  'Reserve Sec': { icon: Archive, color: 'text-amber-500' },
  'Bubble Tea & Emballages': { icon: GlassWater, color: 'text-purple-500' },
  'Sirops': { icon: Candy, color: 'text-pink-500' },
};

// DLC helpers
function getDlcDaysLeft(dlc) {
  if (!dlc) return null;
  const today = new Date(); today.setHours(0, 0, 0, 0);
  const dlcDate = new Date(dlc); dlcDate.setHours(0, 0, 0, 0);
  return Math.ceil((dlcDate - today) / (1000 * 60 * 60 * 24));
}

function DlcBadge({ dlc }) {
  if (!dlc) return <span className="text-xs text-muted-foreground">—</span>;
  const days = getDlcDaysLeft(dlc);
  const formatted = new Date(dlc).toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit' });
  if (days < 0) return <Badge variant="destructive" className="gap-1 text-xs" data-testid="dlc-expired"><Ban className="w-3 h-3" />Exp. {formatted}</Badge>;
  if (days === 0) return <Badge variant="destructive" className="gap-1 text-xs animate-pulse" data-testid="dlc-today"><Clock className="w-3 h-3" />Aujourd'hui</Badge>;
  if (days <= 2) return <Badge className="bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300 gap-1 text-xs" data-testid="dlc-critical"><Clock className="w-3 h-3" />{formatted} ({days}j)</Badge>;
  if (days <= 5) return <Badge className="bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-300 gap-1 text-xs" data-testid="dlc-warning"><Clock className="w-3 h-3" />{formatted} ({days}j)</Badge>;
  return <Badge className="bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300 gap-1 text-xs" data-testid="dlc-ok">{formatted} ({days}j)</Badge>;
}

function DlcInput({ value, onChange }) {
  const [local, setLocal] = useState(value || '');
  const prevValue = useRef(value);
  useEffect(() => { if (value !== prevValue.current) { setLocal(value || ''); prevValue.current = value; } }, [value]);
  const commit = (v) => { const val = v || null; if (val !== (value || null)) onChange(val); };
  return <Input type="date" value={local} onChange={e => setLocal(e.target.value)} onBlur={() => commit(local)} onKeyDown={e => { if (e.key === 'Enter') { e.target.blur(); }}} className="h-7 w-[130px] text-xs" data-testid="dlc-input" />;
}

function UniteInput({ value, onChange }) {
  const [local, setLocal] = useState(value || '');
  const prevValue = useRef(value);
  useEffect(() => { if (value !== prevValue.current) { setLocal(value || ''); prevValue.current = value; } }, [value]);
  const commit = () => { const v = local.trim() || 'unité'; if (v !== value) onChange(v); };
  return <Input value={local} onChange={e => setLocal(e.target.value)} onBlur={commit} onKeyDown={e => { if (e.key === 'Enter') e.target.blur(); }} className="h-7 w-[80px] text-xs text-center" data-testid="unite-input" />;
}

function StockBadge({ stock, seuil }) {
  if (stock <= 0) return <Badge variant="destructive">{stock}</Badge>;
  if (stock <= seuil) return <Badge className="bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-300">{stock}</Badge>;
  return <Badge className="bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300">{stock}</Badge>;
}

function QuickStockEditor({ item, onUpdate }) {
  const [val, setVal] = useState(item.stock);
  useEffect(() => { setVal(item.stock); }, [item.stock]);
  return (
    <div className="flex items-center gap-1">
      <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => { const n = Math.max(0, val - 1); setVal(n); onUpdate(item.id, n); }}><Minus className="w-3 h-3" /></Button>
      <Input type="number" step="0.5" min="0" value={val} onChange={(e) => setVal(parseFloat(e.target.value) || 0)}
        onBlur={() => { if (val !== item.stock) onUpdate(item.id, val); }}
        className="w-16 h-7 text-center text-sm tabular-nums" data-testid={`stock-input-${item.id}`} />
      <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => { const n = val + 1; setVal(n); onUpdate(item.id, n); }}><Plus className="w-3 h-3" /></Button>
    </div>
  );
}

function CategorySelect({ item, categories, onChange }) {
  return (
    <Select value={item.categorie} onValueChange={(v) => onChange(item.id, v)}>
      <SelectTrigger className="h-7 text-xs w-[150px]" data-testid={`cat-select-${item.id}`}><SelectValue /></SelectTrigger>
      <SelectContent>{categories.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
    </Select>
  );
}

function AddItemDialog({ zones, categories, activeZone, onAdd }) {
  const [open, setOpen] = useState(false);
  const [isNewCat, setIsNewCat] = useState(false);
  const [newCatName, setNewCatName] = useState('');
  const [form, setForm] = useState({ zone: '', categorie: '', ingredient: '', stock: 0, unite: 'unité', seuil_alerte: 2, dlc: null });

  useEffect(() => { if (open && activeZone) setForm(f => ({ ...f, zone: activeZone })); }, [open, activeZone]);

  const handleSubmit = () => {
    const cat = isNewCat ? newCatName.trim() : form.categorie;
    if (!form.zone || !cat || !form.ingredient.trim()) { toast.error('Zone, categorie et ingredient requis'); return; }
    onAdd({ ...form, categorie: cat });
    setOpen(false);
    setForm({ zone: activeZone || '', categorie: '', ingredient: '', stock: 0, unite: 'unité', seuil_alerte: 2, dlc: null });
    setIsNewCat(false); setNewCatName('');
  };

  const zoneCats = [...new Set(categories.filter(c => c.zone === form.zone).map(c => c.name))];

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild><Button className="gap-2" data-testid="add-item-btn"><Plus className="w-4 h-4" /> Ajouter un produit</Button></DialogTrigger>
      <DialogContent>
        <DialogHeader><DialogTitle>Ajouter un produit</DialogTitle></DialogHeader>
        <div className="space-y-4">
          <div>
            <Label>Zone</Label>
            <Select value={form.zone} onValueChange={v => { setForm(f => ({ ...f, zone: v, categorie: '' })); setIsNewCat(false); }}>
              <SelectTrigger data-testid="add-zone-select"><SelectValue placeholder="Zone..." /></SelectTrigger>
              <SelectContent>{zones.map(z => <SelectItem key={z} value={z}>{z}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          {form.zone && (
            <div>
              <Label>Categorie</Label>
              <div className="space-y-2">
                {!isNewCat ? (
                  <>
                    {zoneCats.length > 0 && (
                      <Select value={form.categorie} onValueChange={v => setForm(f => ({ ...f, categorie: v }))}>
                        <SelectTrigger data-testid="add-categorie-select"><SelectValue placeholder="Categorie..." /></SelectTrigger>
                        <SelectContent>{zoneCats.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                      </Select>
                    )}
                    <Button variant="outline" size="sm" className="w-full gap-2" onClick={() => setIsNewCat(true)} data-testid="new-cat-btn"><Plus className="w-3 h-3" /> Nouvelle categorie</Button>
                  </>
                ) : (
                  <div className="flex gap-2">
                    <Input autoFocus value={newCatName} onChange={e => setNewCatName(e.target.value)} placeholder="Nouvelle categorie" className="flex-1" data-testid="new-cat-input" />
                    <Button variant="ghost" size="sm" onClick={() => { setIsNewCat(false); setNewCatName(''); }}><X className="w-4 h-4" /></Button>
                  </div>
                )}
              </div>
            </div>
          )}
          <div><Label>Produit</Label><Input value={form.ingredient} onChange={e => setForm(f => ({ ...f, ingredient: e.target.value }))} placeholder="Saumon, Edamame..." data-testid="add-ingredient-input" /></div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Stock</Label><Input type="number" step="0.5" min="0" value={form.stock} onChange={e => setForm(f => ({ ...f, stock: parseFloat(e.target.value) || 0 }))} /></div>
            <div><Label>DLC</Label><Input type="date" value={form.dlc || ''} onChange={e => setForm(f => ({ ...f, dlc: e.target.value || null }))} /></div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Unite</Label><Input value={form.unite} onChange={e => setForm(f => ({ ...f, unite: e.target.value }))} placeholder="kg, L..." /></div>
            <div><Label>Seuil alerte</Label><Input type="number" step="0.5" min="0" value={form.seuil_alerte} onChange={e => setForm(f => ({ ...f, seuil_alerte: parseFloat(e.target.value) || 0 }))} /></div>
          </div>
          <Button onClick={handleSubmit} className="w-full" data-testid="add-confirm-btn">Ajouter</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function ManageCategoriesDialog({ zone, items, onRename, onDeleteCat, onAddCategory }) {
  const [open, setOpen] = useState(false);
  const [editingCat, setEditingCat] = useState(null);
  const [newName, setNewName] = useState('');
  const [addingNew, setAddingNew] = useState(false);
  const [newCatInput, setNewCatInput] = useState('');
  const categories = [...new Set(items.filter(i => i.zone === zone).map(i => i.categorie))];
  const getCatCount = (cat) => items.filter(i => i.zone === zone && i.categorie === cat).length;
  const handleRename = (oldCat) => { if (!newName.trim() || newName.trim() === oldCat) { setEditingCat(null); return; } onRename(zone, oldCat, newName.trim()); setEditingCat(null); };
  const handleAddNew = () => { if (!newCatInput.trim()) return; if (categories.includes(newCatInput.trim())) { toast.error('Existe deja'); return; } onAddCategory(zone, newCatInput.trim()); setNewCatInput(''); setAddingNew(false); };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild><Button variant="outline" size="sm" className="gap-2" data-testid="manage-categories-btn"><Tags className="w-4 h-4" /> Categories</Button></DialogTrigger>
      <DialogContent className="max-w-md">
        <DialogHeader><DialogTitle>Categories - {zone}</DialogTitle></DialogHeader>
        <div className="space-y-1 max-h-[400px] overflow-y-auto" data-testid="categories-list">
          {categories.map(cat => (
            <div key={cat} className="flex items-center gap-2 p-2 rounded-md hover:bg-muted/50">
              {editingCat === cat ? (
                <><Input autoFocus value={newName} onChange={e => setNewName(e.target.value)} className="h-8 text-sm flex-1" onKeyDown={e => { if (e.key === 'Enter') handleRename(cat); if (e.key === 'Escape') setEditingCat(null); }} />
                <Button variant="ghost" size="icon" className="h-7 w-7 text-green-600" onClick={() => handleRename(cat)}><Check className="w-3 h-3" /></Button>
                <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => setEditingCat(null)}><X className="w-3 h-3" /></Button></>
              ) : (
                <><span className="flex-1 text-sm font-medium">{cat}</span><Badge variant="secondary" className="text-xs">{getCatCount(cat)}</Badge>
                <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => { setEditingCat(cat); setNewName(cat); }}><Pencil className="w-3 h-3" /></Button>
                <Button variant="ghost" size="icon" className="h-7 w-7 text-destructive" onClick={() => { if (window.confirm(`Supprimer "${cat}" ?`)) onDeleteCat(zone, cat); }}><Trash2 className="w-3 h-3" /></Button></>
              )}
            </div>
          ))}
        </div>
        <div className="border-t pt-3 mt-2">
          {addingNew ? (
            <div className="flex gap-2">
              <Input autoFocus value={newCatInput} onChange={e => setNewCatInput(e.target.value)} placeholder="Nouvelle categorie" className="flex-1" onKeyDown={e => { if (e.key === 'Enter') handleAddNew(); }} data-testid="add-new-category-input" />
              <Button size="sm" onClick={handleAddNew}><Check className="w-4 h-4" /></Button>
              <Button variant="ghost" size="sm" onClick={() => setAddingNew(false)}><X className="w-4 h-4" /></Button>
            </div>
          ) : (
            <Button variant="outline" className="w-full gap-2" onClick={() => setAddingNew(true)} data-testid="create-category-btn"><Plus className="w-4 h-4" /> Creer une categorie</Button>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

function JeterDialog({ items, onJeter }) {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ ingredient: '', zone: '', categorie: '', quantite: 1, unite: 'unité', raison: '' });
  const [selectedItemId, setSelectedItemId] = useState('');

  const handleSelectItem = (id) => {
    const item = items.find(i => i.id === id);
    if (item) {
      setSelectedItemId(id);
      setForm({ ingredient: item.ingredient, zone: item.zone, categorie: item.categorie, quantite: 1, unite: item.unite, raison: '' });
    }
  };

  const handleSubmit = () => {
    if (!form.ingredient) { toast.error('Selectionnez un produit'); return; }
    onJeter(form, selectedItemId);
    setOpen(false);
    setForm({ ingredient: '', zone: '', categorie: '', quantite: 1, unite: 'unité', raison: '' });
    setSelectedItemId('');
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild><Button variant="outline" className="gap-2 border-red-200 text-red-600 hover:bg-red-50" data-testid="jeter-btn"><Trash2 className="w-4 h-4" /> Declarer un produit jete</Button></DialogTrigger>
      <DialogContent>
        <DialogHeader><DialogTitle>Produit jete</DialogTitle></DialogHeader>
        <div className="space-y-4">
          <div>
            <Label>Produit de l'inventaire</Label>
            <Select value={selectedItemId} onValueChange={handleSelectItem}>
              <SelectTrigger data-testid="jeter-product-select"><SelectValue placeholder="Selectionner un produit..." /></SelectTrigger>
              <SelectContent className="max-h-[300px]">
                {items.map(i => <SelectItem key={i.id} value={i.id}>{i.ingredient} ({i.zone} - {i.categorie})</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Quantite jetee</Label><Input type="number" step="0.5" min="0.5" value={form.quantite} onChange={e => setForm(f => ({ ...f, quantite: parseFloat(e.target.value) || 1 }))} data-testid="jeter-qty" /></div>
            <div><Label>Unite</Label><Input value={form.unite} disabled className="bg-muted" /></div>
          </div>
          <div>
            <Label>Raison</Label>
            <Select value={form.raison} onValueChange={v => setForm(f => ({ ...f, raison: v }))}>
              <SelectTrigger data-testid="jeter-raison"><SelectValue placeholder="Raison..." /></SelectTrigger>
              <SelectContent>
                <SelectItem value="DLC depassee">DLC depassee</SelectItem>
                <SelectItem value="Produit abime">Produit abime</SelectItem>
                <SelectItem value="Contamination">Contamination</SelectItem>
                <SelectItem value="Erreur de preparation">Erreur de preparation</SelectItem>
                <SelectItem value="Surplus">Surplus</SelectItem>
                <SelectItem value="Autre">Autre</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <Button onClick={handleSubmit} className="w-full bg-red-600 hover:bg-red-700" data-testid="jeter-confirm-btn">Confirmer le produit jete</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export function InventairePage() {
  const { api } = useAuth();
  const [items, setItems] = useState([]);
  const [jetes, setJetes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeZone, setActiveZone] = useState('Congelateur');
  const [mainTab, setMainTab] = useState('inventaire');
  const [selectedItems, setSelectedItems] = useState(new Set());
  const [selectedJetes, setSelectedJetes] = useState(new Set());
  const [lastUpdated, setLastUpdated] = useState(null);
  const [snapshots, setSnapshots] = useState([]);
  const [viewSnap, setViewSnap] = useState(null);
  const [snapLoading, setSnapLoading] = useState(false);
  const [importing, setImporting] = useState(false);

  const zones = Object.keys(ZONE_CONFIG);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [invRes, jetRes, luRes] = await Promise.all([api.get('/inventaire'), api.get('/produits-jetes'), api.get('/inventaire/last-updated')]);
      if (invRes.data.length === 0) {
        await api.post('/inventaire/seed');
        const r = await api.get('/inventaire');
        setItems(r.data);
      } else {
        setItems(invRes.data);
      }
      setJetes(jetRes.data);
      setLastUpdated(luRes.data?.last_updated || null);
    } catch { toast.error('Erreur chargement'); }
    finally { setLoading(false); }
  }, [api]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const refreshTimestamp = async () => {
    try { const r = await api.get('/inventaire/last-updated'); setLastUpdated(r.data?.last_updated || null); } catch {}
  };

  const handleStockUpdate = async (id, newStock) => {
    try { await api.put(`/inventaire/${id}/stock`, { stock: newStock }); setItems(prev => prev.map(i => i.id === id ? { ...i, stock: newStock } : i)); refreshTimestamp(); } catch { toast.error('Erreur'); }
  };

  const handleDlcUpdate = async (id, newDlc) => {
    const item = items.find(i => i.id === id);
    if (!item) return;
    try { await api.put(`/inventaire/${id}`, { ...item, dlc: newDlc || null }); setItems(prev => prev.map(i => i.id === id ? { ...i, dlc: newDlc || null } : i)); refreshTimestamp(); } catch { toast.error('Erreur DLC'); }
  };

  const handleUniteUpdate = async (id, newUnite) => {
    try { await api.put(`/inventaire/${id}`, { unite: newUnite }); setItems(prev => prev.map(i => i.id === id ? { ...i, unite: newUnite } : i)); refreshTimestamp(); } catch { toast.error('Erreur unité'); }
  };

  const handleAddItem = async (form) => {
    try { const res = await api.post('/inventaire', form); setItems(prev => [...prev, res.data]); toast.success('Ajouté'); refreshTimestamp(); } catch { toast.error('Erreur'); }
  };
  const handleDelete = async (id) => {
    try { await api.delete(`/inventaire/${id}`); setItems(prev => prev.filter(i => i.id !== id)); toast.success('Supprimé'); refreshTimestamp(); } catch { toast.error('Erreur'); }
  };
  const handleImportFile = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setImporting(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const res = await api.post('/inventaire/import', formData, { headers: { 'Content-Type': 'multipart/form-data' } });
      toast.success(res.data.message);
      fetchData();
    } catch (err) { toast.error(err.response?.data?.detail || "Erreur d'import"); }
    finally { setImporting(false); e.target.value = ''; }
  };
  const handleRenameCategory = async (zone, oldCat, newCat) => {
    try { await api.put('/inventaire/rename-category', { zone, old_name: oldCat, new_name: newCat }); setItems(prev => prev.map(i => (i.zone === zone && i.categorie === oldCat) ? { ...i, categorie: newCat } : i)); toast.success('Renommé'); } catch { toast.error('Erreur'); }
  };
  const handleDeleteCategory = async (zone, cat) => {
    try { await api.delete(`/inventaire/delete-category?zone=${encodeURIComponent(zone)}&categorie=${encodeURIComponent(cat)}`); setItems(prev => prev.filter(i => !(i.zone === zone && i.categorie === cat))); toast.success('Supprimée'); } catch { toast.error('Erreur'); }
  };

  // Snapshot fin de journée
  const fetchSnapshots = async () => {
    try { const res = await api.get('/inventaire/snapshots'); setSnapshots(res.data); } catch {}
  };
  useEffect(() => { fetchSnapshots(); }, []);

  const handleSaveSnapshot = async () => {
    setSnapLoading(true);
    try {
      const res = await api.post('/inventaire/snapshot');
      toast.success(res.data.message);
      fetchSnapshots();
    } catch (err) { toast.error(err.response?.data?.detail || 'Erreur'); }
    finally { setSnapLoading(false); }
  };

  const handleViewSnapshot = async (snapId) => {
    try {
      const res = await api.get(`/inventaire/snapshots/${snapId}`);
      setViewSnap(res.data);
    } catch { toast.error('Erreur'); }
  };

  const handleDeleteSnapshot = async (snapId) => {
    try { await api.delete(`/inventaire/snapshots/${snapId}`); setSnapshots(s => s.filter(x => x.id !== snapId)); toast.success('Supprimé'); if (viewSnap?.id === snapId) setViewSnap(null); } catch { toast.error('Erreur'); }
  };

  const handleRestoreSnapshot = async (snapId) => {
    try {
      const res = await api.post(`/inventaire/snapshots/${snapId}/restore`);
      toast.success(res.data.message);
      setViewSnap(null);
      fetchData();
    } catch (err) { toast.error(err.response?.data?.detail || 'Erreur'); }
  };

  const handleAddCategory = async (zone, catName) => {
    try { const res = await api.post('/inventaire', { zone, categorie: catName, ingredient: '(nouveau)', stock: 0, unite: 'unité', seuil_alerte: 1 }); setItems(prev => [...prev, res.data]); toast.success('Categorie créée'); } catch { toast.error('Erreur'); }
  };
  const handleChangeItemCategory = async (itemId, newCat) => {
    const item = items.find(i => i.id === itemId);
    if (!item || item.categorie === newCat) return;
    try { await api.put(`/inventaire/${itemId}`, { ...item, categorie: newCat }); setItems(prev => prev.map(i => i.id === itemId ? { ...i, categorie: newCat } : i)); toast.success('Catégorie modifiée'); } catch { toast.error('Erreur'); }
  };

  const handleJeter = async (form, sourceItemId) => {
    try {
      const res = await api.post('/produits-jetes', form);
      setJetes(prev => [res.data, ...prev]);
      // Deduct from inventory stock
      if (sourceItemId) {
        const item = items.find(i => i.id === sourceItemId);
        if (item) {
          const newStock = Math.max(0, item.stock - form.quantite);
          await api.put(`/inventaire/${sourceItemId}/stock`, { stock: newStock });
          setItems(prev => prev.map(i => i.id === sourceItemId ? { ...i, stock: newStock } : i));
        }
      }
      toast.success('Produit jeté enregistré');
    } catch { toast.error('Erreur'); }
  };

  const handleDeleteJete = async (id) => {
    try { await api.delete(`/produits-jetes/${id}`); setJetes(prev => prev.filter(j => j.id !== id)); toast.success('Supprimé'); } catch { toast.error('Erreur'); }
  };

  // Bulk delete
  const handleBulkDeleteItems = async () => {
    if (selectedItems.size === 0) return;
    if (!window.confirm(`Supprimer ${selectedItems.size} article(s) ?`)) return;
    try {
      await api.post('/inventaire/bulk-delete', { ids: [...selectedItems] });
      setItems(prev => prev.filter(i => !selectedItems.has(i.id)));
      toast.success(`${selectedItems.size} articles supprimés`);
      setSelectedItems(new Set());
      refreshTimestamp();
    } catch { toast.error('Erreur suppression'); }
  };

  const handleBulkDeleteJetes = async () => {
    if (selectedJetes.size === 0) return;
    if (!window.confirm(`Supprimer ${selectedJetes.size} entrée(s) ?`)) return;
    try {
      await api.post('/produits-jetes/bulk-delete', { ids: [...selectedJetes] });
      setJetes(prev => prev.filter(j => !selectedJetes.has(j.id)));
      toast.success(`${selectedJetes.size} entrées supprimées`);
      setSelectedJetes(new Set());
    } catch { toast.error('Erreur suppression'); }
  };

  const toggleItem = (id) => setSelectedItems(prev => { const s = new Set(prev); s.has(id) ? s.delete(id) : s.add(id); return s; });
  const toggleJete = (id) => setSelectedJetes(prev => { const s = new Set(prev); s.has(id) ? s.delete(id) : s.add(id); return s; });
  const toggleAllZone = (zItems) => {
    const ids = zItems.map(i => i.id);
    const allSelected = ids.every(id => selectedItems.has(id));
    setSelectedItems(prev => { const s = new Set(prev); ids.forEach(id => allSelected ? s.delete(id) : s.add(id)); return s; });
  };
  const toggleAllJetes = () => {
    const allSelected = jetes.every(j => selectedJetes.has(j.id));
    setSelectedJetes(allSelected ? new Set() : new Set(jetes.map(j => j.id)));
  };

  const allCategories = [...new Set(items.map(i => JSON.stringify({ zone: i.zone, name: i.categorie })))].map(s => JSON.parse(s));
  const totalItems = items.length;
  const alertCount = items.filter(i => i.stock <= i.seuil_alerte).length;
  const outOfStock = items.filter(i => i.stock <= 0).length;
  const dlcCritical = items.filter(i => { const d = getDlcDaysLeft(i.dlc); return d !== null && d <= 2; }).length;

  return (
    <div className="space-y-6" data-testid="inventaire-page">
      <div className="module-header">
        <div>
          <h1 className="module-title flex items-center gap-3" data-testid="inventaire-title"><Package className="w-8 h-8 text-primary" /> Inventaire</h1>
          <p className="text-muted-foreground mt-1">Stock, DLC et produits jetés</p>
          {lastUpdated && (
            <p className="text-xs text-muted-foreground mt-1" data-testid="last-updated-text">
              Dernière mise à jour : {new Date(lastUpdated).toLocaleDateString('fr-FR', { day: '2-digit', month: 'long', year: 'numeric', hour: '2-digit', minute: '2-digit' })}
            </p>
          )}
        </div>
        <div className="flex gap-2 flex-wrap">
          <SnapshotButtons module="inventaire" moduleLabel="Inventaire" />
          <ExportButtons api={api} moduleKey="inventaire" />
          <Dialog>
            <DialogTrigger asChild>
              <Button variant="outline" className="gap-2" data-testid="import-inventaire-btn">
                <Upload className="w-4 h-4" /> Importer
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader><DialogTitle>Importer l'inventaire</DialogTitle></DialogHeader>
              <div className="space-y-4">
                <p className="text-sm text-muted-foreground">
                  Importez un fichier CSV ou XLSX avec les colonnes :<br/>
                  <strong>Zone, Catégorie, Produit/Ingrédient, Stock, Unité, Seuil alerte, DLC</strong>
                </p>
                <p className="text-xs text-muted-foreground">Seule la colonne "Produit" (ou "Ingrédient" ou "Nom") est obligatoire. Les autres sont optionnelles.</p>
                <Input type="file" accept=".csv,.xlsx,.xls" onChange={handleImportFile} disabled={importing} data-testid="import-inventaire-file" />
                {importing && <div className="flex items-center gap-2 text-sm text-muted-foreground"><Loader2 className="w-4 h-4 animate-spin" /> Import en cours...</div>}
              </div>
            </DialogContent>
          </Dialog>
          <Button variant="outline" onClick={fetchData} disabled={loading} data-testid="refresh-btn"><RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} /> Actualiser</Button>
          <JeterDialog items={items} onJeter={handleJeter} />
          <AddItemDialog zones={zones} categories={allCategories} activeZone={activeZone} onAdd={handleAddItem} />
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card className="stat-card"><CardContent className="p-4">
          <p className="text-xs text-muted-foreground mb-1">Total articles</p>
          <p className="text-2xl font-bold tabular-nums" data-testid="total-items">{totalItems}</p>
        </CardContent></Card>
        <Card className={`stat-card ${alertCount > 0 ? 'border-amber-300' : ''}`}><CardContent className="p-4">
          <div className="flex items-center gap-2 mb-1"><AlertTriangle className="w-4 h-4 text-amber-500" /><p className="text-xs text-muted-foreground">Stock faible</p></div>
          <p className="text-2xl font-bold tabular-nums text-amber-600" data-testid="alert-count">{alertCount}</p>
        </CardContent></Card>
        <Card className={`stat-card ${dlcCritical > 0 ? 'border-red-300' : ''}`}><CardContent className="p-4">
          <div className="flex items-center gap-2 mb-1"><Clock className="w-4 h-4 text-red-500" /><p className="text-xs text-muted-foreground">DLC critique</p></div>
          <p className="text-2xl font-bold tabular-nums text-red-600" data-testid="dlc-critical-count">{dlcCritical}</p>
        </CardContent></Card>
        <Card className="stat-card border-gray-200"><CardContent className="p-4">
          <div className="flex items-center gap-2 mb-1"><Trash2 className="w-4 h-4 text-gray-500" /><p className="text-xs text-muted-foreground">Produits jetés</p></div>
          <p className="text-2xl font-bold tabular-nums text-gray-600" data-testid="jetes-count">{jetes.length}</p>
        </CardContent></Card>
      </div>

      {/* Main tabs: Inventaire vs Produits Jetés */}
      <Tabs value={mainTab} onValueChange={setMainTab}>
        <TabsList>
          <TabsTrigger value="inventaire" className="gap-2" data-testid="main-tab-inventaire"><Package className="w-4 h-4" /> Inventaire</TabsTrigger>
          <TabsTrigger value="valorisation" className="gap-2" data-testid="main-tab-valorisation"><Euro className="w-4 h-4" /> Valorisation</TabsTrigger>
          <TabsTrigger value="receptions" className="gap-2" data-testid="main-tab-receptions"><Truck className="w-4 h-4" /> Réceptions</TabsTrigger>
          <TabsTrigger value="historique" className="gap-2" data-testid="main-tab-historique"><History className="w-4 h-4" /> Historique</TabsTrigger>
          <TabsTrigger value="jetes" className="gap-2" data-testid="main-tab-jetes"><Trash2 className="w-4 h-4" /> Produits Jetés <Badge variant={jetes.length > 0 ? 'destructive' : 'secondary'} className="ml-1 text-xs">{jetes.length}</Badge></TabsTrigger>
          <TabsTrigger value="snapshots" className="gap-2" data-testid="main-tab-snapshots"><Camera className="w-4 h-4" /> Sauvegardes <Badge variant="secondary" className="ml-1 text-xs">{snapshots.length}</Badge></TabsTrigger>
        </TabsList>

        <TabsContent value="inventaire">
          <Tabs value={activeZone} onValueChange={setActiveZone}>
            <TabsList className="flex flex-wrap h-auto gap-1">
              {zones.map(zone => {
                const config = ZONE_CONFIG[zone]; const Icon = config.icon;
                const zCount = items.filter(i => i.zone === zone).length;
                const zAlert = items.filter(i => i.zone === zone && i.stock <= i.seuil_alerte).length;
                const zDlc = items.filter(i => i.zone === zone && getDlcDaysLeft(i.dlc) !== null && getDlcDaysLeft(i.dlc) <= 2).length;
                return (
                  <TabsTrigger key={zone} value={zone} className="gap-1.5 text-xs" data-testid={`tab-${zone}`}>
                    <Icon className={`w-4 h-4 ${config.color}`} />
                    <span className="hidden sm:inline">{zone}</span>
                    <Badge variant="secondary" className="ml-1 text-xs">{zCount}</Badge>
                    {zAlert > 0 && <Badge variant="destructive" className="text-xs">{zAlert}</Badge>}
                    {zDlc > 0 && <Badge className="bg-red-100 text-red-800 text-xs ml-0.5">DLC</Badge>}
                  </TabsTrigger>
                );
              })}
            </TabsList>
            {zones.map(zone => {
              const config = ZONE_CONFIG[zone];
              const zItems = items.filter(i => i.zone === zone);
              const zCategories = [...new Set(zItems.map(i => i.categorie))];
              const zSelected = zItems.filter(i => selectedItems.has(i.id)).length;
              const allZoneSelected = zItems.length > 0 && zSelected === zItems.length;
              return (
                <TabsContent key={zone} value={zone}>
                  <Card>
                    <CardHeader className="pb-2 flex flex-row items-center justify-between">
                      <CardTitle className="text-base flex items-center gap-2">
                        {(() => { const Icon = config.icon; return <Icon className={`w-5 h-5 ${config.color}`} />; })()}
                        {zone} ({zItems.length})
                      </CardTitle>
                      <div className="flex items-center gap-2">
                        {zSelected > 0 && (
                          <Button variant="destructive" size="sm" className="gap-1" onClick={handleBulkDeleteItems} data-testid="bulk-delete-items">
                            <Trash2 className="w-3 h-3" /> Supprimer ({zSelected})
                          </Button>
                        )}
                        <ManageCategoriesDialog zone={zone} items={items} onRename={handleRenameCategory} onDeleteCat={handleDeleteCategory} onAddCategory={handleAddCategory} />
                      </div>
                    </CardHeader>
                    <CardContent className="p-0">
                      <div className="overflow-y-auto" style={{ maxHeight: 'calc(100vh - 480px)', minHeight: '300px' }} data-testid={`zone-scroll-${zone}`}>
                        <Table>
                          <TableHeader className="sticky top-0 bg-card z-10">
                            <TableRow>
                              <TableHead className="w-[40px]"><Checkbox checked={allZoneSelected} onCheckedChange={() => toggleAllZone(zItems)} data-testid="select-all-zone" /></TableHead>
                              <TableHead className="w-[160px]">Categorie</TableHead>
                              <TableHead>Ingredient</TableHead>
                              <TableHead className="text-center w-[140px]">DLC</TableHead>
                              <TableHead className="text-center w-[70px]">Stock</TableHead>
                              <TableHead className="w-[55px]">Unite</TableHead>
                              <TableHead className="text-center w-[150px]">Modifier stock</TableHead>
                              <TableHead className="w-[40px]"></TableHead>
                            </TableRow>
                          </TableHeader>
                          <TableBody>
                            {zItems.length === 0 ? (
                              <TableRow><TableCell colSpan={8} className="text-center py-8 text-muted-foreground">Aucun article</TableCell></TableRow>
                            ) : (
                              zItems.map(item => {
                                const dlcDays = getDlcDaysLeft(item.dlc);
                                const dlcRow = dlcDays !== null && dlcDays <= 0 ? 'bg-red-50/70 dark:bg-red-950/20' : dlcDays !== null && dlcDays <= 2 ? 'bg-red-50/40 dark:bg-red-950/10' : item.stock <= 0 ? 'bg-red-50/50 dark:bg-red-950/10' : item.stock <= item.seuil_alerte ? 'bg-amber-50/30 dark:bg-amber-950/10' : '';
                                return (
                                  <TableRow key={item.id} className={`${dlcRow} ${selectedItems.has(item.id) ? 'bg-primary/5' : ''}`}>
                                    <TableCell><Checkbox checked={selectedItems.has(item.id)} onCheckedChange={() => toggleItem(item.id)} data-testid={`select-${item.id}`} /></TableCell>
                                    <TableCell><CategorySelect item={item} categories={zCategories} onChange={handleChangeItemCategory} /></TableCell>
                                    <TableCell className="font-medium">{item.ingredient}</TableCell>
                                    <TableCell className="text-center">
                                      <div className="flex items-center gap-1 justify-center">
                                        <DlcInput value={item.dlc} onChange={(v) => handleDlcUpdate(item.id, v)} />
                                        <DlcBadge dlc={item.dlc} />
                                      </div>
                                    </TableCell>
                                    <TableCell className="text-center"><StockBadge stock={item.stock} seuil={item.seuil_alerte} /></TableCell>
                                    <TableCell><UniteInput value={item.unite} onChange={(v) => handleUniteUpdate(item.id, v)} /></TableCell>
                                    <TableCell><QuickStockEditor item={item} onUpdate={handleStockUpdate} /></TableCell>
                                    <TableCell><Button variant="ghost" size="icon" className="h-7 w-7 text-destructive" onClick={() => handleDelete(item.id)}><Trash2 className="w-3 h-3" /></Button></TableCell>
                                  </TableRow>
                                );
                              })
                            )}
                          </TableBody>
                        </Table>
                      </div>
                    </CardContent>
                  </Card>
                </TabsContent>
              );
            })}
          </Tabs>
        </TabsContent>

        {/* VALORISATION TAB */}
        <TabsContent value="valorisation">
          <ValorisationTab />
        </TabsContent>

        {/* RECEPTIONS TAB */}
        <TabsContent value="receptions">
          <ReceptionsTab />
        </TabsContent>

        {/* HISTORIQUE TAB */}
        <TabsContent value="historique">
          <HistoriqueTab />
        </TabsContent>

        {/* PRODUITS JETES TAB */}
        <TabsContent value="jetes">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle className="text-base flex items-center gap-2"><Trash2 className="w-5 h-5 text-red-500" /> Historique des produits jetés ({jetes.length})</CardTitle>
              {selectedJetes.size > 0 && (
                <Button variant="destructive" size="sm" className="gap-1" onClick={handleBulkDeleteJetes} data-testid="bulk-delete-jetes">
                  <Trash2 className="w-3 h-3" /> Supprimer ({selectedJetes.size})
                </Button>
              )}
            </CardHeader>
            <CardContent className="p-0">
              <div className="overflow-y-auto" style={{ maxHeight: 'calc(100vh - 420px)', minHeight: '200px' }}>
                <Table>
                  <TableHeader className="sticky top-0 bg-card z-10">
                    <TableRow>
                      <TableHead className="w-[40px]"><Checkbox checked={jetes.length > 0 && selectedJetes.size === jetes.length} onCheckedChange={toggleAllJetes} data-testid="select-all-jetes" /></TableHead>
                      <TableHead>Date</TableHead>
                      <TableHead>Produit</TableHead>
                      <TableHead>Zone</TableHead>
                      <TableHead>Categorie</TableHead>
                      <TableHead className="text-center">Quantite</TableHead>
                      <TableHead>Raison</TableHead>
                      <TableHead className="w-[40px]"></TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {jetes.length === 0 ? (
                      <TableRow><TableCell colSpan={8} className="text-center py-8 text-muted-foreground">Aucun produit jeté enregistré</TableCell></TableRow>
                    ) : (
                      jetes.map(j => (
                        <TableRow key={j.id} className={selectedJetes.has(j.id) ? 'bg-primary/5' : ''} data-testid={`jete-row-${j.id}`}>
                          <TableCell><Checkbox checked={selectedJetes.has(j.id)} onCheckedChange={() => toggleJete(j.id)} data-testid={`select-jete-${j.id}`} /></TableCell>
                          <TableCell className="text-sm">{j.date ? new Date(j.date).toLocaleDateString('fr-FR') : '—'}</TableCell>
                          <TableCell className="font-medium">{j.ingredient}</TableCell>
                          <TableCell><Badge variant="outline" className="text-xs">{j.zone}</Badge></TableCell>
                          <TableCell className="text-sm text-muted-foreground">{j.categorie}</TableCell>
                          <TableCell className="text-center font-medium text-red-600">{j.quantite} {j.unite}</TableCell>
                          <TableCell><Badge variant="secondary" className="text-xs">{j.raison || '—'}</Badge></TableCell>
                          <TableCell><Button variant="ghost" size="icon" className="h-7 w-7 text-destructive" onClick={() => handleDeleteJete(j.id)}><X className="w-3 h-3" /></Button></TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* ====== TAB SNAPSHOTS ====== */}
        <TabsContent value="snapshots">
          {viewSnap ? (
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle>Snapshot du {new Date(viewSnap.date).toLocaleDateString('fr-FR')} à {viewSnap.heure}</CardTitle>
                    <p className="text-sm text-muted-foreground mt-1">{viewSnap.nb_articles} articles</p>
                  </div>
                  <div className="flex gap-2">
                    <AlertDialog>
                      <AlertDialogTrigger asChild>
                        <Button className="gap-2" data-testid="restore-snapshot-btn">
                          <RefreshCw className="w-4 h-4" /> Restaurer cet inventaire
                        </Button>
                      </AlertDialogTrigger>
                      <AlertDialogContent>
                        <AlertDialogHeader>
                          <AlertDialogTitle>Restaurer l'inventaire ?</AlertDialogTitle>
                          <AlertDialogDescription>
                            L'inventaire actuel sera remplacé par celui du {viewSnap && new Date(viewSnap.date).toLocaleDateString('fr-FR')} à {viewSnap?.heure}. Un snapshot de l'état actuel sera créé automatiquement avant la restauration.
                          </AlertDialogDescription>
                        </AlertDialogHeader>
                        <AlertDialogFooter>
                          <AlertDialogCancel>Annuler</AlertDialogCancel>
                          <AlertDialogAction onClick={() => handleRestoreSnapshot(viewSnap.id)} data-testid="confirm-restore">Restaurer</AlertDialogAction>
                        </AlertDialogFooter>
                      </AlertDialogContent>
                    </AlertDialog>
                    <Button variant="outline" onClick={() => setViewSnap(null)} data-testid="back-snapshots-btn">Retour</Button>
                  </div>
                </div>
              </CardHeader>
              <CardContent className="p-0">
                <ScrollArea className="h-[500px]">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Zone</TableHead>
                        <TableHead>Catégorie</TableHead>
                        <TableHead>Produit</TableHead>
                        <TableHead className="text-center">Stock</TableHead>
                        <TableHead>Unité</TableHead>
                        <TableHead>DLC</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {(viewSnap.items || []).map((item, idx) => (
                        <TableRow key={idx}>
                          <TableCell><Badge variant="outline">{item.zone}</Badge></TableCell>
                          <TableCell className="text-sm text-muted-foreground">{item.categorie}</TableCell>
                          <TableCell className="font-medium">{item.ingredient}</TableCell>
                          <TableCell className="text-center"><StockBadge stock={item.stock} seuil={item.seuil_alerte} /></TableCell>
                          <TableCell className="text-sm">{item.unite}</TableCell>
                          <TableCell><DlcBadge dlc={item.dlc} /></TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </ScrollArea>
              </CardContent>
            </Card>
          ) : (
            <Card>
              <CardHeader>
                <CardTitle>Sauvegardes fin de journée</CardTitle>
              </CardHeader>
              <CardContent>
                {snapshots.length === 0 ? (
                  <p className="text-center py-8 text-muted-foreground">Aucune sauvegarde. Cliquez sur "Sauvegarder" pour créer un snapshot de l'inventaire actuel.</p>
                ) : (
                  <div className="space-y-2">
                    {snapshots.map(snap => (
                      <div key={snap.id} className="flex items-center justify-between p-3 rounded-lg border hover:bg-muted/50" data-testid={`snapshot-${snap.id}`}>
                        <div>
                          <p className="font-medium">
                            {new Date(snap.date).toLocaleDateString('fr-FR', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })}
                            {snap.auto && <span className="ml-2 text-xs px-2 py-0.5 rounded-full bg-blue-100 text-blue-700">Auto</span>}
                          </p>
                          <p className="text-sm text-muted-foreground">{snap.heure} — {snap.nb_articles} articles</p>
                        </div>
                        <div className="flex gap-2">
                          <Button variant="outline" size="sm" className="gap-1" onClick={() => handleViewSnapshot(snap.id)} data-testid={`view-snap-${snap.id}`}>
                            <Eye className="w-4 h-4" /> Voir
                          </Button>
                          <Button variant="ghost" size="sm" className="text-destructive" onClick={() => handleDeleteSnapshot(snap.id)} data-testid={`delete-snap-${snap.id}`}>
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
}
